import { useState, useEffect, useCallback } from 'react';
import { useNotify, useGetOne, useRefresh } from 'react-admin';
import { channelConnectionApi, type TelegramBotStatus } from '../services/api.service';
import type { ChannelConnection } from '../types/chat.types';

export interface UseTelegramBotOptions {
  channelConnectionId: number;
  onStatusChange?: (isConnected: boolean) => void;
}

export interface UseTelegramBotReturn {
  connection: ChannelConnection | null;
  loading: boolean;
  actionLoading: string | null;
  isConnected: boolean;
  botUsername: string | null;
  botFirstName: string | null;
  botStatus: TelegramBotStatus | null;
  botStatusLoading: boolean;
  refetch: () => void;
  refetchBotStatus: () => void;
  setupWebhook: () => Promise<void>;
  disconnect: () => Promise<void>;
}

export function useTelegramBot({
  channelConnectionId,
  onStatusChange,
}: UseTelegramBotOptions): UseTelegramBotReturn {
  const notify = useNotify();
  const refresh = useRefresh();
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [botStatus, setBotStatus] = useState<TelegramBotStatus | null>(null);
  const [botStatusLoading, setBotStatusLoading] = useState(false);

  // Use React Admin's useGetOne to fetch the channel connection
  const { data: connection, isLoading, refetch } = useGetOne<ChannelConnection>(
    'channel-connection',
    { id: channelConnectionId },
    { enabled: !!channelConnectionId }
  );

  const isConnected = connection?.status === 'connected';
  const metadata = connection?.metadata as Record<string, unknown> | undefined;
  const metaBotUsername = metadata?.botUsername as string | undefined;
  const metaBotFirstName = metadata?.botFirstName as string | undefined;
  const botUsername = metaBotUsername ? `@${metaBotUsername}` : metaBotFirstName || null;
  const botFirstName = metaBotFirstName || null;

  // Fetch live bot status from Telegram API
  const fetchBotStatus = useCallback(async () => {
    if (!isConnected) {
      setBotStatus(null);
      return;
    }
    try {
      setBotStatusLoading(true);
      const response = await channelConnectionApi.getTelegramBotStatus(channelConnectionId);
      setBotStatus(response.data);
    } catch {
      setBotStatus(null);
    } finally {
      setBotStatusLoading(false);
    }
  }, [channelConnectionId, isConnected]);

  useEffect(() => {
    fetchBotStatus();
  }, [fetchBotStatus]);

  const setupWebhook = useCallback(async () => {
    try {
      setActionLoading('setup');
      const response = await channelConnectionApi.setupTelegramWebhook(channelConnectionId);
      notify('resources.channel.telegram.notifications.connected', { type: 'success' });
      onStatusChange?.(true);
      // Refresh the connection data to get updated status
      refetch();
      refresh();
      // Also refresh bot status after setup
      fetchBotStatus();
    } catch {
      notify('resources.channel.telegram.notifications.setupFailed', { type: 'error' });
    } finally {
      setActionLoading(null);
    }
  }, [channelConnectionId, notify, onStatusChange, refetch, refresh, fetchBotStatus]);

  const disconnect = useCallback(async () => {
    try {
      setActionLoading('disconnect');
      await channelConnectionApi.disconnectTelegram(channelConnectionId);
      notify('resources.channel.telegram.notifications.disconnected', { type: 'success' });
      onStatusChange?.(false);
      // Refresh the connection data to get updated status
      refetch();
      refresh();
      setBotStatus(null);
    } catch {
      notify('resources.channel.telegram.notifications.disconnectFailed', { type: 'error' });
    } finally {
      setActionLoading(null);
    }
  }, [channelConnectionId, notify, onStatusChange, refetch, refresh]);

  return {
    connection: connection ?? null,
    loading: isLoading,
    actionLoading,
    isConnected,
    botUsername,
    botFirstName,
    botStatus,
    botStatusLoading,
    refetch,
    refetchBotStatus: fetchBotStatus,
    setupWebhook,
    disconnect,
  };
}
